/**
 * Nurse MAR (Medication Administration Record) Dashboard
 * 
 * Zero Re-Entry Workflow:
 * - New meds appear automatically
 * - Administration schedule pre-built
 * - Education talking points generated
 * - PRN effectiveness tracking enabled
 * 
 * Nurse does NOT:
 * - Re-enter meds
 * - Call pharmacy
 * - Re-type orders
 * - Chase signatures
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Pill, CheckCircle, XCircle, Clock, AlertTriangle,
  User, RefreshCw, Search, Calendar, Activity,
  ClipboardCheck, Heart, FileText, Timer, Info,
  AlertCircle, ChevronRight, BookOpen, ThumbsUp,
  ThumbsDown, HelpCircle, Minus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// MAR Status icons and colors
const STATUS_CONFIG = {
  scheduled: { icon: Clock, color: "text-blue-600", bg: "bg-blue-100" },
  due: { icon: AlertCircle, color: "text-amber-600", bg: "bg-amber-100" },
  administered: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-100" },
  held: { icon: Minus, color: "text-gray-600", bg: "bg-gray-100" },
  refused: { icon: XCircle, color: "text-red-600", bg: "bg-red-100" },
  missed: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100" },
  not_available: { icon: HelpCircle, color: "text-gray-600", bg: "bg-gray-100" }
};

// PRN Effectiveness options
const PRN_EFFECTIVENESS = [
  { value: "effective", label: "Effective", icon: ThumbsUp, color: "text-green-600" },
  { value: "partially_effective", label: "Partially Effective", icon: Minus, color: "text-amber-600" },
  { value: "not_effective", label: "Not Effective", icon: ThumbsDown, color: "text-red-600" }
];

export default function NurseMARDashboard({ patientId, patientName }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // MAR data
  const [marData, setMarData] = useState(null);
  const [marSummary, setMarSummary] = useState(null);
  const [educationMaterials, setEducationMaterials] = useState(null);
  
  // Administration dialog
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
  const [adminStatus, setAdminStatus] = useState("administered");
  const [adminNotes, setAdminNotes] = useState("");
  const [reasonNotGiven, setReasonNotGiven] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // PRN dialog
  const [showPRNDialog, setShowPRNDialog] = useState(false);
  const [selectedPRN, setSelectedPRN] = useState(null);
  const [prnReason, setPrnReason] = useState("");
  const [prnNotes, setPrnNotes] = useState("");
  
  // PRN reassessment
  const [showReassessDialog, setShowReassessDialog] = useState(false);
  const [reassessAdminId, setReassessAdminId] = useState(null);
  const [prnEffectiveness, setPrnEffectiveness] = useState("");
  const [reassessNotes, setReassessNotes] = useState("");

  // Fetch MAR data
  const fetchMARData = useCallback(async () => {
    if (!patientId) return;
    
    try {
      const [marRes, summaryRes, eduRes] = await Promise.all([
        fetch(`${API}/api/mar/patient/${patientId}`),
        fetch(`${API}/api/mar/patient/${patientId}/summary`),
        fetch(`${API}/api/mar/patient/${patientId}/education`)
      ]);
      
      if (marRes.ok) {
        const mar = await marRes.json();
        setMarData(mar);
      }
      
      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setMarSummary(summary);
      }
      
      if (eduRes.ok) {
        const edu = await eduRes.json();
        setEducationMaterials(edu);
      }
    } catch (error) {
      console.error("Error fetching MAR data:", error);
      toast.error("Failed to load MAR data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchMARData();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchMARData, 60000);
    return () => clearInterval(interval);
  }, [fetchMARData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMARData();
  };

  // Open administration dialog
  const handleAdminister = (med, scheduleItem = null) => {
    setSelectedMed(med);
    setSelectedScheduleItem(scheduleItem);
    setAdminStatus("administered");
    setAdminNotes("");
    setReasonNotGiven("");
    setShowAdminDialog(true);
  };

  // Submit administration
  const handleSubmitAdmin = async () => {
    if (!selectedMed) return;
    
    setSubmitting(true);
    try {
      const scheduledTime = selectedScheduleItem?.full_datetime || new Date().toISOString();
      
      const response = await fetch(`${API}/api/mar/administer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mar_medication_id: selectedMed.id || selectedScheduleItem?.medication_id,
          scheduled_time: scheduledTime,
          status: adminStatus,
          notes: adminNotes || null,
          reason_not_given: adminStatus !== "administered" ? reasonNotGiven : null
        })
      });
      
      if (response.ok) {
        toast.success(`Medication ${adminStatus === 'administered' ? 'administered' : 'recorded'} successfully`);
        setShowAdminDialog(false);
        fetchMARData();
      } else {
        toast.error("Failed to record administration");
      }
    } catch (error) {
      console.error("Admin error:", error);
      toast.error("Error recording administration");
    } finally {
      setSubmitting(false);
    }
  };

  // Open PRN dialog
  const handleAdministerPRN = (med) => {
    setSelectedPRN(med);
    setPrnReason("");
    setPrnNotes("");
    setShowPRNDialog(true);
  };

  // Submit PRN administration
  const handleSubmitPRN = async () => {
    if (!selectedPRN || !prnReason) {
      toast.error("Please provide a reason for PRN administration");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API}/api/mar/prn/administer/${selectedPRN.id}?reason=${encodeURIComponent(prnReason)}` +
        (prnNotes ? `&notes=${encodeURIComponent(prnNotes)}` : ''),
        { method: "POST" }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`PRN medication administered. Reassessment due in 1 hour.`);
        setShowPRNDialog(false);
        
        // Set up reassessment reminder
        if (result.administration_id) {
          setReassessAdminId(result.administration_id);
        }
        
        fetchMARData();
      } else {
        toast.error("Failed to administer PRN");
      }
    } catch (error) {
      console.error("PRN error:", error);
      toast.error("Error administering PRN");
    } finally {
      setSubmitting(false);
    }
  };

  // Submit PRN reassessment
  const handleSubmitReassess = async () => {
    if (!reassessAdminId || !prnEffectiveness) {
      toast.error("Please select effectiveness level");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API}/api/mar/prn/reassess/${reassessAdminId}?effectiveness=${prnEffectiveness}` +
        (reassessNotes ? `&notes=${encodeURIComponent(reassessNotes)}` : ''),
        { method: "POST" }
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.escalation_recommended) {
          toast.warning("PRN not effective - escalation alert created for physician review");
        } else {
          toast.success("PRN effectiveness documented");
        }
        setShowReassessDialog(false);
        fetchMARData();
      } else {
        toast.error("Failed to record reassessment");
      }
    } catch (error) {
      console.error("Reassess error:", error);
      toast.error("Error recording reassessment");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.scheduled;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!patientId) {
    return (
      <div className="text-center py-12 text-gray-500">
        <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">Select a patient</p>
        <p className="text-sm">Choose a patient to view their MAR</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Pill className="h-6 w-6 text-blue-600" />
            Medication Administration Record
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {patientName || 'Patient'} • Auto-synced with pharmacy
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {marSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-gray-500">Total Meds</p>
              <p className="text-2xl font-bold">{marSummary.total_medications || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-gray-500">Scheduled</p>
              <p className="text-2xl font-bold">{marSummary.scheduled_medications || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-gray-500">PRN</p>
              <p className="text-2xl font-bold">{marSummary.prn_medications || 0}</p>
            </CardContent>
          </Card>
          <Card className={marSummary.due_now > 0 ? 'border-amber-500 border-2' : ''}>
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-gray-500">Due Now</p>
              <p className={`text-2xl font-bold ${marSummary.due_now > 0 ? 'text-amber-600' : ''}`}>
                {marSummary.due_now || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-gray-500">Given Today</p>
              <p className="text-2xl font-bold text-green-600">{marSummary.administered_today || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts */}
      {marData?.alerts?.length > 0 && (
        <Card className="border-red-500 border-l-4">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">Medication Alerts</p>
            </div>
            <div className="space-y-2">
              {marData.alerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Badge variant="destructive">{alert.type}</Badge>
                  <p>{alert.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Today's Schedule
            </CardTitle>
            <CardDescription>Scheduled medication times</CardDescription>
          </CardHeader>
          <CardContent>
            {marData?.today_schedule?.length > 0 ? (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {marData.today_schedule.map((item, idx) => {
                    const status = getStatusConfig(item.status);
                    const StatusIcon = status.icon;
                    const isPast = new Date(`${new Date().toISOString().split('T')[0]}T${item.scheduled_time}`) < new Date();
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          item.status === 'administered' ? 'bg-green-50' :
                          item.status === 'missed' ? 'bg-red-50' :
                          isPast && item.status === 'scheduled' ? 'bg-amber-50' :
                          'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${status.bg}`}>
                            <StatusIcon className={`h-4 w-4 ${status.color}`} />
                          </div>
                          <div>
                            <p className="font-medium">{item.medication_name}</p>
                            <p className="text-sm text-gray-500">
                              {item.dose} • {item.route} • {item.scheduled_time}
                            </p>
                          </div>
                        </div>
                        {item.status === 'scheduled' && (
                          <Button 
                            size="sm"
                            onClick={() => handleAdminister(null, item)}
                            className={isPast ? 'bg-amber-600 hover:bg-amber-700' : ''}
                          >
                            {isPast ? 'Overdue' : 'Administer'}
                          </Button>
                        )}
                        {item.status === 'administered' && (
                          <div className="text-right text-sm text-gray-500">
                            <p>{item.administered_by}</p>
                            <p>{item.administered_at?.split('T')[1]?.slice(0,5)}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No scheduled medications for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PRN Medications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              PRN Medications
            </CardTitle>
            <CardDescription>As-needed medications with effectiveness tracking</CardDescription>
          </CardHeader>
          <CardContent>
            {marData?.medications?.prn?.length > 0 ? (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {marData.medications.prn.map((med) => (
                    <Card key={med.id} className="border-l-4 border-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{med.medication_name}</p>
                            <p className="text-sm text-gray-500">
                              {med.dose} • {med.route}
                            </p>
                            {med.prn_reason && (
                              <Badge variant="outline" className="mt-1 text-xs">
                                For: {med.prn_reason}
                              </Badge>
                            )}
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => handleAdministerPRN(med)}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            Give PRN
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No PRN medications</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Education Materials */}
      {educationMaterials?.medications?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-green-600" />
              Medication Education
            </CardTitle>
            <CardDescription>Talking points for patient/caregiver education</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {educationMaterials.medications.map((edu) => (
                <Card key={edu.medication_id} className="bg-gray-50">
                  <CardContent className="pt-4">
                    <p className="font-medium text-sm">{edu.medication_name}</p>
                    <p className="text-xs text-gray-500 mb-2">
                      {edu.dose} • {edu.route} • {edu.frequency}
                    </p>
                    <Separator className="my-2" />
                    <div className="space-y-1">
                      {edu.talking_points?.slice(0, 3).map((point, idx) => (
                        <p key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                          <Info className="h-3 w-3 mt-0.5 text-blue-500" />
                          {point}
                        </p>
                      ))}
                    </div>
                    {edu.side_effects_to_monitor?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-amber-600">Watch for:</p>
                        <p className="text-xs text-gray-500">
                          {edu.side_effects_to_monitor.join(", ")}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Administration Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Administration</DialogTitle>
            <DialogDescription>
              {selectedMed?.medication_name || selectedScheduleItem?.medication_name}
              {selectedScheduleItem?.scheduled_time && ` • ${selectedScheduleItem.scheduled_time}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={adminStatus} onValueChange={setAdminStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="administered">Administered</SelectItem>
                  <SelectItem value="held">Held</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                  <SelectItem value="not_available">Not Available</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {adminStatus !== "administered" && (
              <div>
                <label className="text-sm font-medium">Reason Not Given</label>
                <Textarea 
                  value={reasonNotGiven}
                  onChange={(e) => setReasonNotGiven(e.target.value)}
                  placeholder="Document reason..."
                  rows={2}
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitAdmin} disabled={submitting}>
              {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRN Administration Dialog */}
      <Dialog open={showPRNDialog} onOpenChange={setShowPRNDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Administer PRN Medication</DialogTitle>
            <DialogDescription>
              {selectedPRN?.medication_name} • {selectedPRN?.dose}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reason for PRN *</label>
              <Input 
                value={prnReason}
                onChange={(e) => setPrnReason(e.target.value)}
                placeholder="e.g., Pain 6/10, Nausea, Anxiety..."
              />
            </div>

            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={prnNotes}
                onChange={(e) => setPrnNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-blue-700 text-sm">
                  <Timer className="h-4 w-4" />
                  <p>Reassessment will be due in 1 hour</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPRNDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitPRN} 
              disabled={submitting || !prnReason}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Administer PRN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRN Reassessment Dialog */}
      <Dialog open={showReassessDialog} onOpenChange={setShowReassessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PRN Reassessment</DialogTitle>
            <DialogDescription>
              Document medication effectiveness
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Effectiveness *</label>
              <div className="grid grid-cols-3 gap-2">
                {PRN_EFFECTIVENESS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <Button
                      key={opt.value}
                      variant={prnEffectiveness === opt.value ? "default" : "outline"}
                      className={`flex flex-col h-auto py-3 ${
                        prnEffectiveness === opt.value ? '' : opt.color
                      }`}
                      onClick={() => setPrnEffectiveness(opt.value)}
                    >
                      <Icon className="h-5 w-5 mb-1" />
                      <span className="text-xs">{opt.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={reassessNotes}
                onChange={(e) => setReassessNotes(e.target.value)}
                placeholder="Patient response, additional observations..."
                rows={2}
              />
            </div>

            {prnEffectiveness === "not_effective" && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    <p>An escalation alert will be created for physician review</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassessDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitReassess} 
              disabled={submitting || !prnEffectiveness}
            >
              {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Submit Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
